import React from 'react';
import { Link } from 'react-router-dom';

import { Product } from 'interfaces/interfaces';

interface TableProps {
  data: Product[];
}

const Table: React.FC<TableProps> = ({ data }) => {
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);

  const pageCount = Math.max(1, Math.ceil(data.length / pageSize));
  const currentPageIndex = Math.min(pageIndex, pageCount - 1);
  const startIndex = currentPageIndex * pageSize;
  const pageData = React.useMemo(() => data.slice(startIndex, startIndex + pageSize), [data, startIndex, pageSize]);

  React.useEffect(() => {
    setPageIndex((current) => Math.min(current, Math.max(pageCount - 1, 0)));
  }, [pageCount]);

  const canPreviousPage = currentPageIndex > 0;
  const canNextPage = currentPageIndex < pageCount - 1;
  const startRow = data.length === 0 ? 0 : startIndex + 1;
  const endRow = data.length === 0 ? 0 : Math.min(startIndex + pageData.length, data.length);

  return (
    <div className="inActiveContent">
      <table className="table table-light borderless productTable">
        <thead>
          <tr>
            <th className="product-col" scope="col">
              Product
            </th>
            <th scope="col">&nbsp;</th>
            <th scope="col">&nbsp;</th>
            <th scope="col" className="amount amount-col">
              Amount Paid
            </th>
            <th className="duedate-col" scope="col">
              Paid Through
            </th>
          </tr>
        </thead>
        <tbody>
          {pageData.map((row) => (
            <tr key={row.policyNumber}>
              <td>
                <Link to={`/product-details/${row.policyNumber}`}>
                  <span className="anchorText">{row.productDesc}</span>
                </Link>
                <span className="smallTableText">{row.policyNumber}</span>
              </td>
              <td>&nbsp;</td>
              <td>&nbsp;</td>
              <td className="amount">{row.amountDue}</td>
              <td>{row.dueDate}</td>
            </tr>
          ))}
        </tbody>
        {data.length >= 10 && (
          <tfoot>
            <tr>
              <td colSpan={5}>
                <div className="pagination">
                  <span className="rowPages">Rows per page: </span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPageIndex(0);
                    }}
                  >
                    {[10, 20, 30, 40, 50].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span className="middlePaginationContent">
                    {startRow} - {endRow} of {data.length}
                  </span>
                  <button onClick={() => setPageIndex(0)} disabled={!canPreviousPage}>
                    <span className="materialicons">first_page</span>
                  </button>
                  <button onClick={() => setPageIndex((current) => current - 1)} disabled={!canPreviousPage}>
                    <span className="materialicons">chevron_left</span>
                  </button>
                  <button onClick={() => setPageIndex((current) => current + 1)} disabled={!canNextPage}>
                    <span className="materialicons">chevron_right</span>
                  </button>
                  <button onClick={() => setPageIndex(pageCount - 1)} disabled={!canNextPage}>
                    <span className="materialicons">last_page</span>
                  </button>
                </div>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

interface InactiveProductsProps {
  inactiveProducts: Product[];
}

export const InactiveProducts: React.FC<InactiveProductsProps> = ({ inactiveProducts }) => {
  return <Table data={inactiveProducts} />;
};
